'use client'

import { useSiteConfig } from '@/feature/config/hooks/useSiteConfig.client'
import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { InfoCard } from '@/ui/cards/info-card/InfoCard'
import { Drawer, DrawerBody, DrawerHeading } from '@/ui/drawer/drawer/Drawer.client'
import { TextInputSkeleton } from '@/ui/form/inputs/text/TextInputSkeleton'
import { TextSkeleton } from '@/ui/skeleton/TextSkeleton'
import type { ApolloError } from '@apollo/client'
import type { PrimitiveAtom, WritableAtom } from 'jotai'
import { atom, useAtom } from 'jotai'
import { MIN_POINTS_OF_SALE_FOR_POSTAL_CODE_SEARCH } from '../../../services/pointOfSale.constants'
import { PointOfSalePostalCodeSearchInput } from '../../point-of-sale-postal-code-search-input/PointOfSalePostalCodeSearchInput.client'
import { SelectablePointOfSaleCardSkeleton } from '../../selectable-point-of-sale-card/SelectablePointOfSaleCardSkeleton'

type PointsOfSaleDrawer_V2Props<
  T extends { __typename?: 'ProductPointOfSale' | 'PointOfSale'; id?: string },
> = {
  isOpenAtom: WritableAtom<boolean, [boolean], void> | PrimitiveAtom<boolean>
  pointsOfSale: T[] | undefined | null
  error: ApolloError | undefined
  isLoading: boolean
  renderPointOfSale: (pos: T) => React.ReactNode
  searchPostalCode?: string
  onSearchPostalCodeChangeAction?: (code: string) => void
}

/**
 * A noop atom to avoid passing undefined to useAtom
 */
const noopAtom = atom(false)

export default function SelectPointsOfSaleDrawer<
  T extends { __typename?: 'ProductPointOfSale' | 'PointOfSale'; id: string },
>({ isOpenAtom, pointsOfSale, isLoading: loading, error, ...rest }: PointsOfSaleDrawer_V2Props<T>) {
  const { t } = useTranslation(['selectPointsOfSaleDrawer'])
  const { config } = useSiteConfig()
  const [isOpen, setIsOpen] = useAtom(isOpenAtom || noopAtom)

  const { renderPointOfSale } = rest

  // We only show search input if there are multiple points of sale (> limit)
  // or
  const hasMultiplePointsOfSaleOrActiveSearch =
    (rest.searchPostalCode?.length ?? 0) > 0 ||
    (pointsOfSale?.length ?? 0) > MIN_POINTS_OF_SALE_FOR_POSTAL_CODE_SEARCH

  // We prevent showing the title and search input if the country config isn't available
  const showSearchInput =
    hasMultiplePointsOfSaleOrActiveSearch && !!config?.site?.defaultCountryIso2

  return (
    <Drawer title={t('selectPointsOfSaleDrawer:title')} isOpen={isOpen} onOpenChange={setIsOpen}>
      {showSearchInput && rest.onSearchPostalCodeChangeAction && (
        <DrawerHeading>
          {loading && (!pointsOfSale || pointsOfSale.length === 0) ? (
            <>
              <TextSkeleton size="md" leading="normal" className="mb-1 w-1/2" />
              <div className="mt-1">
                <TextInputSkeleton />
              </div>
            </>
          ) : (
            <>
              {t('selectPointsOfSaleDrawer:search.title')}
              <div className="mt-1">
                <PointOfSalePostalCodeSearchInput
                  defaultValue={rest.searchPostalCode}
                  onChange={rest.onSearchPostalCodeChangeAction}
                  isLoading={loading}
                  error={error}
                />
              </div>
            </>
          )}
        </DrawerHeading>
      )}
      <DrawerBody>
        {error && !pointsOfSale?.length && (
          <InfoCard variant="error" content={t('selectPointsOfSaleDrawer:error')} />
        )}
        <ul
          className={twMerge(
            'flex flex-col gap-2 transition-opacity duration-200',
            loading && pointsOfSale && pointsOfSale.length > 0 && 'pointer-events-none opacity-40',
          )}
        >
          {loading && !pointsOfSale?.length
            ? Array.from({ length: 4 }).map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={i}>
                  <SelectablePointOfSaleCardSkeleton />
                </li>
              ))
            : pointsOfSale?.map((pos) => <li key={pos.id}>{renderPointOfSale(pos)}</li>)}
        </ul>
      </DrawerBody>
    </Drawer>
  )
}
